import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowLeft,
  Check,
  Phone,
  Settings2,
  Layers,
  ShieldCheck,
  MessageSquare,
  X,
  Send,
  Mail,
  User,
} from "lucide-react";
import Seo from "@/components/Seo";
import { LogoMark } from "@/components/Logo";
import ty from "@/locales/thankyou.json";

const WORKSPACE_IMG =
  "https://images.pexels.com/photos/7369488/pexels-photo-7369488.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=520&w=1200";

const MONTHS = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];

function formatStamp(d) {
  const day = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${day} de ${MONTHS[d.getMonth()]} de ${d.getFullYear()} · ${hh}:${mm}`;
}

/** BBVA Argentina lockup, drawn inline so it never renders as a broken image. */
const BankMark = () => (
  <span
    aria-label={ty.trust.bankName}
    className="inline-flex items-center gap-2.5 rounded-lg bg-[#004481] px-3 py-2"
  >
    <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
      <path
        d="M4 20V9l8-5 8 5v11"
        fill="none"
        stroke="#ffffff"
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M9 20v-6h6v6" fill="none" stroke="#4DB2E5" strokeWidth="1.9" strokeLinejoin="round" />
    </svg>
    <span className="font-mono text-[0.82rem] font-semibold tracking-[0.18em] text-white">
      BBVA
    </span>
  </span>
);

const useCountUp = (target, active) => {
  const reduce = useReducedMotion();
  const [value, setValue] = useState(reduce ? target : 0);
  useEffect(() => {
    if (!active || reduce) {
      setValue(target);
      return undefined;
    }
    let raf;
    const start = performance.now();
    const duration = 1200;
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => raf && cancelAnimationFrame(raf);
  }, [target, active, reduce]);
  return value;
};

const StatValue = ({ stat }) => {
  const count = useCountUp(stat.value, true);
  const display =
    stat.value >= 1000 && stat.display.includes("K")
      ? `${Math.round(count / 1000)}K+`
      : String(count);
  return (
    <span className="font-display text-[1.6rem] font-semibold leading-none text-ink">
      {display}
    </span>
  );
};

const NEXT_ICONS = [Phone, Settings2, Layers];

export default function ThankYou() {
  const reduce = useReducedMotion();
  const [step, setStep] = useState(1);
  const [feedCount, setFeedCount] = useState(4);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatBadge, setChatBadge] = useState(1);
  const [shownMessages, setShownMessages] = useState(0);
  const [typing, setTyping] = useState(false);
  const [userMessages, setUserMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const msgEndRef = useRef(null);

  const stamp = useMemo(() => formatStamp(new Date()), []);

  useEffect(() => {
    const t1 = window.setTimeout(() => setStep(2), 3200);
    const t2 = window.setTimeout(() => setStep(3), 7400);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, []);

  useEffect(() => {
    if (feedCount >= ty.liveFeed.items.length) return undefined;
    const id = window.setTimeout(() => setFeedCount((c) => c + 1), 4200);
    return () => window.clearTimeout(id);
  }, [feedCount]);

  useEffect(() => {
    if (!chatOpen) return undefined;
    setChatBadge(0);
    if (shownMessages >= ty.chat.messages.length) return undefined;
    setTyping(true);
    const id = window.setTimeout(() => {
      setTyping(false);
      setShownMessages((n) => n + 1);
    }, 900);
    return () => window.clearTimeout(id);
  }, [chatOpen, shownMessages]);

  useEffect(() => {
    if (msgEndRef.current) {
      msgEndRef.current.scrollIntoView({ block: "end", behavior: "smooth" });
    }
  }, [shownMessages, userMessages, typing]);

  const sendChat = (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text) return;
    setUserMessages((m) => [...m, { text, reply: ty.chat.autoReply }]);
    setDraft("");
  };

  const progressPct = step === 1 ? 8 : step === 2 ? 50 : 100;
  const statusText =
    step === 1
      ? ty.progress.status1
      : step === 2
        ? ty.progress.status2
        : ty.progress.status3;

  const feedItems = ty.liveFeed.items.slice(0, feedCount);

  return (
    <div className="min-h-screen bg-paper">
      <Seo
        title={ty.meta.title}
        description={ty.meta.description}
        htmlLang="es-AR"
        canonical="https://databridgar.com/thank_you"
      />

      {/* top bar */}
      <header
        data-no-break="true"
        className="border-b border-hairline bg-paper/90 backdrop-blur"
      >
        <div className="mx-auto flex w-full max-w-3xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <Link
            to="/ar"
            data-testid="thankyou-logo-link"
            className="flex items-center gap-2.5"
          >
            <LogoMark className="h-8 w-8" title="Databridgar" />
            <span className="text-[1rem] font-semibold text-ink">Databridgar</span>
          </Link>
          <Link
            to="/ar"
            data-testid="thankyou-back-home"
            className="inline-flex items-center gap-1.5 rounded-xl border border-hairline bg-white px-3 py-2 text-[0.78rem] font-medium text-ink transition-colors duration-200 hover:bg-paper2"
          >
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2} />
            {ty.hero.backHome}
          </Link>
        </div>
      </header>

      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden border-b border-hairline">
        <div className="pointer-events-none absolute inset-0 hero-wash" aria-hidden="true" />
        <div className="relative mx-auto w-full max-w-3xl px-4 py-12 text-center sm:px-6 sm:py-16">
          <span
            data-testid="thankyou-status-badge"
            className="inline-flex items-center gap-2 rounded-full border border-hairline bg-white px-3.5 py-1.5 font-mono text-[0.6rem] uppercase tracking-[0.16em] text-muted-ink"
          >
            <span className="relative flex h-2 w-2">
              {!reduce && (
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-70" />
              )}
              <span className="relative inline-flex h-2 w-2 rounded-full bg-success" />
            </span>
            {ty.hero.badge}
          </span>

          <h1 className="mx-auto mt-6 max-w-[22ch] font-display text-[2.1rem] font-semibold leading-[1.08] tracking-[-0.025em] text-ink sm:text-[2.7rem]">
            {ty.hero.title}
          </h1>
          <p className="mx-auto mt-4 max-w-[46ch] text-[0.95rem] leading-7 text-muted-ink">
            {ty.hero.subtitle}
          </p>
          <p
            data-testid="thankyou-timestamp"
            className="mt-4 font-mono text-[0.66rem] uppercase tracking-[0.12em] text-muted-ink/70"
          >
            {stamp}
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <a
              href="mailto:hola@databridgar.com"
              data-testid="thankyou-write-us"
              className="inline-flex items-center gap-2 rounded-xl bg-ink px-5 py-3 text-[0.85rem] font-semibold text-white transition-colors duration-200 hover:bg-ink2"
            >
              <Mail className="h-4 w-4" strokeWidth={1.9} />
              {ty.hero.writeUs}
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto w-full max-w-3xl px-4 py-8 sm:px-6 sm:py-10">
        {/* ===== PROGRESS ===== */}
        <div
          data-testid="thankyou-progress"
          className="rounded-2xl border border-hairline bg-white p-6 shadow-card sm:p-7"
        >
          <p className="font-mono text-[0.6rem] uppercase tracking-[0.18em] text-muted-ink/70">
            {ty.progress.label}
          </p>
          <div className="relative mt-7">
            <div className="absolute left-[16.666%] right-[16.666%] top-[13px] h-[2px] bg-hairline" />
            <motion.div
              className="absolute left-[16.666%] top-[13px] h-[2px] bg-amber"
              initial={false}
              animate={{ width: `${(progressPct / 100) * 66.666}%` }}
              transition={{ duration: reduce ? 0 : 0.8, ease: [0.22, 1, 0.36, 1] }}
            />
            <div className="relative grid grid-cols-3">
              {[ty.progress.step1, ty.progress.step2, ty.progress.step3].map(
                (label, i) => {
                  const done = step >= i + 1;
                  return (
                    <div key={label} className="flex flex-col items-center">
                      <span
                        data-testid={`thankyou-step-${i + 1}`}
                        className={`flex h-7 w-7 items-center justify-center rounded-full border transition-colors duration-300 ${
                          done
                            ? "border-ink bg-ink text-white"
                            : "border-hairline bg-white text-hairline"
                        }`}
                      >
                        <Check className="h-3.5 w-3.5" strokeWidth={2.4} />
                      </span>
                      <span className="mt-2.5 whitespace-pre-line text-center text-[0.72rem] leading-4 text-muted-ink">
                        {label}
                      </span>
                    </div>
                  );
                },
              )}
            </div>
          </div>
          <div className="mt-6 flex items-center justify-center gap-2 border-t border-hairline2 pt-5">
            <p
              data-testid="thankyou-progress-status"
              className="text-[0.85rem] font-semibold text-ink"
            >
              {statusText}
            </p>
            {step < 3 && !reduce && (
              <span className="flex gap-1" aria-hidden="true">
                {[0, 1, 2].map((d) => (
                  <span
                    key={d}
                    className="dot-pulse h-1.5 w-1.5 rounded-full bg-amber"
                    style={{ animationDelay: `${d * 0.18}s` }}
                  />
                ))}
              </span>
            )}
          </div>
        </div>

        {/* ===== CALL ALERT ===== */}
        <div className="mt-5 rounded-2xl border border-hairline bg-white p-6 shadow-card sm:p-7">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-paper2">
              <Phone className="h-4 w-4 text-amber" strokeWidth={1.9} />
            </span>
            <h2 className="font-display text-[1.2rem] font-semibold leading-snug text-ink">
              {ty.callAlert.title}
            </h2>
          </div>
          <p className="mt-4 text-[0.89rem] leading-7 text-muted-ink">
            {ty.callAlert.body}
          </p>
          <div className="mt-5 border-l-2 border-amber bg-paper2 px-4 py-4">
            <p className="text-[0.78rem] font-semibold uppercase tracking-[0.1em] text-ink">
              {ty.callAlert.noticeTitle}
            </p>
            <p className="mt-2 text-[0.83rem] leading-6 text-muted-ink">
              {ty.callAlert.noticeBody}
            </p>
          </div>
        </div>

        {/* ===== LIVE FEED ===== */}
        <div
          data-testid="thankyou-live-feed"
          className="mt-5 rounded-2xl border border-hairline bg-white p-6 shadow-card sm:p-7"
        >
          <div className="flex items-center gap-2.5 border-b border-hairline2 pb-4">
            <span className="relative flex h-2 w-2">
              {!reduce && (
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-70" />
              )}
              <span className="relative inline-flex h-2 w-2 rounded-full bg-success" />
            </span>
            <p className="font-mono text-[0.6rem] uppercase tracking-[0.18em] text-muted-ink/80">
              {ty.liveFeed.title}
            </p>
          </div>
          <ul className="mt-1">
            {feedItems.map((f) => (
              <motion.li
                key={f.initials + f.minutes}
                initial={reduce ? false : { opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="flex items-center gap-3 border-b border-hairline2 py-3.5 last:border-0"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-paper2 font-mono text-[0.68rem] font-semibold text-ink">
                  {f.initials}
                </span>
                <p className="flex-1 text-[0.84rem] leading-5 text-muted-ink">
                  <strong className="font-semibold text-ink">{f.name}</strong>{" "}
                  {`de ${f.city} ${ty.liveFeed.suffix}`}
                </p>
                <span className="shrink-0 font-mono text-[0.62rem] uppercase tracking-[0.1em] text-muted-ink/70">
                  {`${ty.liveFeed.timePrefix} ${f.minutes} ${ty.liveFeed.timeUnit}`}
                </span>
              </motion.li>
            ))}
          </ul>
        </div>

        {/* ===== WHAT NEXT ===== */}
        <div className="mt-5 rounded-2xl border border-hairline bg-paper2 p-6 sm:p-7">
          <h2 className="font-display text-[1.3rem] font-semibold leading-snug text-ink">
            {ty.next.title}
          </h2>
          <ol className="mt-5">
            {ty.next.steps.map((s, i) => {
              const Icon = NEXT_ICONS[i % NEXT_ICONS.length];
              return (
                <li
                  key={s.title}
                  className="flex gap-4 border-b border-hairline py-5 first:pt-0 last:border-0 last:pb-0"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-hairline bg-white">
                    <Icon className="h-4 w-4 text-ink" strokeWidth={1.8} />
                  </span>
                  <div>
                    <h3 className="text-[0.95rem] font-semibold text-ink">
                      {s.title}
                    </h3>
                    <p className="mt-1.5 text-[0.85rem] leading-6 text-muted-ink">
                      {s.body}
                    </p>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>

        {/* ===== TRUST ===== */}
        <div className="mt-5 overflow-hidden rounded-2xl border border-hairline bg-white shadow-card">
          <div className="flex items-center gap-4 border-b border-hairline2 p-6 sm:p-7">
            <BankMark />
            <div>
              <p className="text-[0.92rem] font-semibold text-ink">
                {ty.trust.title}
              </p>
              <p className="mt-0.5 text-[0.78rem] text-muted-ink">
                {ty.trust.bankTagline}
              </p>
            </div>
          </div>
          <div className="p-6 sm:p-7">
            <p className="text-[0.88rem] leading-7 text-muted-ink">
              {ty.trust.body}
            </p>
            <div
              data-testid="thankyou-trust-stats"
              className="mt-6 grid grid-cols-3 gap-3"
            >
              {ty.trust.stats.map((s) => (
                <div
                  key={s.label}
                  className="rounded-xl border border-hairline bg-paper p-4 text-center"
                >
                  {s.value === 2019 ? (
                    <span className="font-display text-[1.6rem] font-semibold leading-none text-ink">
                      {s.display}
                    </span>
                  ) : (
                    <StatValue stat={s} />
                  )}
                  <p className="mt-2 text-[0.7rem] leading-4 text-muted-ink">
                    {s.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
          <img
            src={WORKSPACE_IMG}
            alt={ty.trust.photoAlt}
            loading="lazy"
            className="h-[200px] w-full object-cover"
          />
          <div className="flex items-center gap-2.5 border-t border-hairline2 p-5">
            <ShieldCheck className="h-4 w-4 shrink-0 text-amber" strokeWidth={1.9} />
            <p className="text-[0.78rem] text-muted-ink">{ty.trust.footer}</p>
          </div>
        </div>

        {/* ===== DISCLAIMER ===== */}
        <p className="mt-5 rounded-2xl border border-hairline bg-paper2 px-5 py-4 text-center text-[0.72rem] leading-6 text-muted-ink/90">
          {ty.disclaimer}
        </p>

        {/* ===== FOOTER LINKS ===== */}
        <div className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 border-t border-hairline pt-6">
          <Link
            to="/ar/privacy"
            data-testid="thankyou-privacy-link"
            className="link-underline text-[0.76rem] text-muted-ink hover:text-ink"
          >
            Política de Privacidad
          </Link>
          <Link
            to="/ar/cookies"
            data-testid="thankyou-cookies-link"
            className="link-underline text-[0.76rem] text-muted-ink hover:text-ink"
          >
            Política de Cookies
          </Link>
          <Link
            to="/ar/terms"
            data-testid="thankyou-terms-link"
            className="link-underline text-[0.76rem] text-muted-ink hover:text-ink"
          >
            Términos y Condiciones
          </Link>
        </div>
      </div>

      {/* ===== CHAT WIDGET ===== */}
      <button
        type="button"
        onClick={() => setChatOpen((v) => !v)}
        aria-label={chatOpen ? ty.chat.close : ty.chat.open}
        data-testid="thankyou-chat-open"
        className="fixed bottom-5 right-5 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-ink text-white shadow-cardHover transition-colors duration-200 hover:bg-ink2"
      >
        {chatOpen ? (
          <X className="h-5 w-5" strokeWidth={2} />
        ) : (
          <MessageSquare className="h-5 w-5" strokeWidth={1.9} />
        )}
        {!chatOpen && chatBadge > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-amber font-mono text-[0.62rem] font-bold text-ink">
            {chatBadge}
          </span>
        )}
      </button>

      {chatOpen && (
        <div
          data-testid="thankyou-chat-window"
          className="fixed bottom-24 right-4 z-40 flex max-h-[70vh] w-[calc(100vw-2rem)] max-w-sm flex-col overflow-hidden rounded-2xl border border-hairline bg-white shadow-cardHover"
        >
          <div className="flex items-center gap-3 border-b border-hairline bg-paper2 px-4 py-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white">
              <User className="h-4 w-4 text-ink" strokeWidth={1.8} />
            </span>
            <div className="flex-1">
              <p className="text-[0.85rem] font-semibold text-ink">
                {ty.chat.headerTitle}
              </p>
              <p className="flex items-center gap-1.5 text-[0.68rem] text-muted-ink">
                <span className="h-1.5 w-1.5 rounded-full bg-success" />
                {ty.chat.headerStatus}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setChatOpen(false)}
              aria-label={ty.chat.close}
              data-testid="thankyou-chat-close"
              className="rounded-lg p-1.5 text-muted-ink transition-colors duration-200 hover:bg-white"
            >
              <X className="h-4 w-4" strokeWidth={1.9} />
            </button>
          </div>

          <div className="flex-1 space-y-2.5 overflow-y-auto p-4">
            {ty.chat.messages.slice(0, shownMessages).map((m) => (
              <p
                key={m}
                className="max-w-[85%] rounded-2xl rounded-bl-md bg-paper2 px-3.5 py-2.5 text-[0.82rem] leading-6 text-ink"
              >
                {m}
              </p>
            ))}
            {userMessages.map((u, i) => (
              <React.Fragment key={`${u.text}-${i}`}>
                <p className="ml-auto max-w-[85%] rounded-2xl rounded-br-md bg-ink px-3.5 py-2.5 text-[0.82rem] leading-6 text-white">
                  {u.text}
                </p>
                <p className="max-w-[85%] rounded-2xl rounded-bl-md bg-paper2 px-3.5 py-2.5 text-[0.82rem] leading-6 text-ink">
                  {u.reply}
                </p>
              </React.Fragment>
            ))}
            {typing && (
              <span className="inline-flex gap-1 rounded-full bg-paper2 px-3 py-2.5">
                {[0, 1, 2].map((d) => (
                  <span
                    key={d}
                    className="dot-pulse h-1.5 w-1.5 rounded-full bg-muted-ink"
                    style={{ animationDelay: `${d * 0.18}s` }}
                  />
                ))}
              </span>
            )}
            <div ref={msgEndRef} />
          </div>

          <form
            onSubmit={sendChat}
            className="flex items-center gap-2 border-t border-hairline p-3"
          >
            <input
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={ty.chat.placeholder}
              data-testid="thankyou-chat-input"
              className="min-w-0 flex-1 rounded-xl border border-hairline px-3 py-2.5 text-[0.82rem] text-ink outline-none transition-colors duration-200 placeholder:text-muted-ink/50 focus:border-ink"
            />
            <button
              type="submit"
              aria-label={ty.chat.send}
              data-testid="thankyou-chat-send"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-ink text-white transition-colors duration-200 hover:bg-ink2"
            >
              <Send className="h-4 w-4" strokeWidth={1.9} />
            </button>
          </form>
          <a
            href="mailto:hola@databridgar.com"
            data-testid="thankyou-chat-mailto"
            className="link-underline border-t border-hairline2 px-4 py-2.5 text-center text-[0.72rem] text-muted-ink hover:text-ink"
          >
            {ty.chat.mailtoLabel}
          </a>
        </div>
      )}
    </div>
  );
}
